import { ModelType } from "~/enums/modelType";
import { IDefaultVOption } from "~/interfaces/vSelectInterfaces";
import { commonFilterMappingFormElements } from "~/models/mapping/mappingFormElementSupport";
import ModelMappingOrigin from "~/models/mapping/modelMappingOrigin";
import ModelMappingValueOrigin from "~/models/mapping/modelMappingValueOrigin";
import Form from "../../../formability/form_builder/models/form";
import FormElement from "../../../formability/form_builder/models/formElements/formElement";
import FormElementDefinition from "../../definitions/formElementDefinition";
import { FormElementType } from "../../enums/formElementType";
import { PaymentType } from "../../enums/workflow/paymentType";
import { WorkflowType } from "../../enums/forms/workflowSteps/workflowType";
import {
  PaymentInformationType,
  PaymentInformationTypeDisplay
} from "../../../formability/mappingBuilder/enums/payment-information-type";
import {
  SubmissionInformationType,
  SubmissionInformationTypeDisplay
} from "../../../formability/mappingBuilder/enums/submission-information-type";

interface INonFormElementValues {
  client_id: string;
  defaultLabelTitle: string;
  mappingLabelTitle: string;
  label_title: string;
  element_type: null;
  submissionInformation: boolean;
}

export default class ModelMappingOriginForm extends ModelMappingOrigin<Form> {
  isSingle: boolean = true;

  get origin(): Form {
    return Form.repo.find(this.id);
  }

  get identifier(): string {
    return this.origin?.client_id
  }

  get title(): string {
    return this.origin?.displayTitle
  }

  get values(): Array<FormElement|INonFormElementValues> {
    return [
      ...this.formElementValues,
      ...this.submissionInformationValues,
    ]
  }

  get formElementValues(): FormElement[] {
    return this.origin?.getFormElements?.filter(formElement => {
      return commonFilterMappingFormElements(formElement) && formElement.repeatableAncestors.length === 0
    })
  }

  get submissionInformationValues(): INonFormElementValues[] {
    const values = []
    for (let key in SubmissionInformationType) {
      if (Number(key) && (Number(key) !== SubmissionInformationType.WorkflowStepCompletion) && (Number(key) !== SubmissionInformationType.WorkflowStepStart)) {
        const value = {
          client_id: key,
          defaultLabelTitle: SubmissionInformationTypeDisplay[key].label,
          label_title: SubmissionInformationTypeDisplay[key].label,
          mappingLabelTitle: SubmissionInformationTypeDisplay[key].label,
          element_type: null,
          submissionInformation: true,
        }
        values.push(value)
      }
    }
    return [...values, ...this.workflowStepCompletedValues, ...this.paymentStepCompletedValues]
  }

  get workflowStepCompletedValues(): INonFormElementValues[] {
    let values = []
    for (let step in this.origin.workflow_steps) {
      let ws = this.origin.workflow_steps[step]
      if (ws.workflow_type === WorkflowType.mapping) continue;
      const value = [
        {
          client_id: `${SubmissionInformationType.WorkflowStepStart}__${ws.client_id}`,
          defaultLabelTitle: `Start Date of ${ws.titleOrDefault}`,
          mappingLabelTitle: `Start Date of ${ws.titleOrDefault}`,
          label_title: `Start Date of ${ws.titleOrDefault}`,
          element_type: null,
          submissionInformation: true,
        },
        {
          client_id: `${SubmissionInformationType.WorkflowStepCompletion}__${ws.client_id}`,
          defaultLabelTitle: `Completion Date of ${ws.titleOrDefault}`,
          mappingLabelTitle: `Completion Date of ${ws.titleOrDefault}`,
          label_title: `Completion Date of ${ws.titleOrDefault}`,
          element_type: null,
          submissionInformation: true,
        }
      ]
      values = values.concat(value)
    }
    return values
  }

  get paymentStepCompletedValues(): INonFormElementValues[] {
    let values = []
    for (let step in this.origin.workflow_steps) {
      let ws = this.origin.workflow_steps[step]
      if (ws.workflow_type !== WorkflowType.payment) continue;
      const value = [
        {
          client_id: `${PaymentInformationType.StepCompleterName}__${ws.client_id}`,
          defaultLabelTitle: `Step Completer Name for ${ws.titleOrDefault}`,
          mappingLabelTitle: `Step Completer Name for ${ws.titleOrDefault}`,
          label_title: `Step Completer Name for ${ws.titleOrDefault}`,
          element_type: "paymentInformation",
          submissionInformation: false,
        },
        {
          client_id: `${PaymentInformationType.TotalPaidFor}__${ws.client_id}`,
          defaultLabelTitle: `Total Paid for ${ws.titleOrDefault}`,
          mappingLabelTitle: `Total Paid for ${ws.titleOrDefault}`,
          label_title: `Total Paid for ${ws.titleOrDefault}`,
          element_type: "paymentInformation",
          submissionInformation: false,
        }
      ]

      if (ws.payment_type !== PaymentType.check) {
        value.push({
          client_id: `${PaymentInformationType.ReceiptNumberFor}__${ws.client_id}`,
          defaultLabelTitle: `Receipt Number for ${ws.titleOrDefault}`,
          mappingLabelTitle: `Receipt Number for ${ws.titleOrDefault}`,
          label_title: `Receipt Number for ${ws.titleOrDefault}`,
          element_type: "paymentInformation",
          submissionInformation: false,
        })
      }

      values = values.concat(value)
    }
    return values
  }

  searchValues(query?: string, options: { filters?: ((value: string) => boolean)[], showUnmappedOnly?: boolean } = {}): Partial<this['values']> {
    let filters = [value => value.mappingLabelTitle?.toLowerCase()?.trim()?.includes(query?.toLowerCase()?.trim())];
    if (options.filters) {
      filters = filters.concat(options.filters);
    }
    if (options.showUnmappedOnly) {
      filters.unshift(value => {
        return !this.modelMapping.isMapped(value.client_id)
      })
    }
    return this.values.filter(value => {
      // If model mapping destination is SObject and Not single, do not allow uploader FormElement values
      // !(disallowedElement) && passesFilters
      return !(
        value instanceof FormElement &&
        value.isTypeOneOf([FormElementType.uploadFiles, FormElementType.imageUpload]) &&
        this.modelMapping?.destinationOrigin?.mappingOriginIs(ModelType.SObject) &&
        !this.modelMapping.destinationOrigin.isSingle
      ) && filters.every(f => f(value));
    })
  }

  valueOptions(source: ModelMappingValueOrigin = null): IDefaultVOption[] {
    if (!this.origin) return [];
    return this.values.reduce((acc, fe) => {
      if (fe instanceof FormElement && !fe.isTypeOneOf([FormElementType.uploadFiles, FormElementType.imageUpload])) {
        acc.push({
          label: fe.mappingLabelTitle || `Untitled ${FormElementDefinition.getDisplayName(fe.element_type)}`,
          value: fe.client_id,
          labelSuffix: this.labelSuffixForSubmissionInformation(fe) || `(${FormElementDefinition.getDisplayName(fe.element_type)})`
        });
      }
      return acc;
    }, []);
  }

  get uploaderValueOptions(): IDefaultVOption[] {
    if (!this.origin) return [];
    return this.formElementValues.reduce((acc, fe) => {
      if (fe.isTypeOneOf([FormElementType.uploadFiles, FormElementType.imageUpload])) {
        acc.push({
          label: fe.mappingLabelTitle || `Untitled ${FormElementDefinition.getDisplayName(fe.element_type)}`,
          value: fe.client_id,
          labelSuffix: this.labelSuffixForSubmissionInformation(fe) || FormElementDefinition.getDisplayName(fe.element_type)
        });
      }
      return acc;
    }, []);
  }

  labelSuffixForSubmissionInformation(value): string {
    if (value.client_id.includes('__')) {
      return SubmissionInformationTypeDisplay[value.client_id.split('__')[0]]?.type
    } else {
      return SubmissionInformationTypeDisplay[value.client_id]?.type
    }
  }

  labelSuffixForPaymentInformation(value): string {
    if (value.client_id.includes('__')) {
      return PaymentInformationTypeDisplay[value.client_id.split('__')[0]]?.type
    } else {
      return PaymentInformationTypeDisplay[value.client_id]?.type
    }
  }

  mapValueToOption(value): IDefaultVOption {
    let suffix: string;
    if (value.element_type === 'paymentInformation') {
      suffix = `(${this.labelSuffixForPaymentInformation(value)})`
    } else if (value.element_type === null) {
      suffix = `(${this.labelSuffixForSubmissionInformation(value)})`
    } else {
      suffix = `(${FormElementDefinition.getDisplayName(value.element_type)})`
    }

    let element_type;
    switch (value.element_type) {
      case null: {
        element_type = 'submissionInformation';
        break;
      }
      case 'paymentInformation': {
        element_type = 'paymentInformation';
        break;
      }
      default: {
        element_type = null;
      }
    }

    return {
      label: value.mappingLabelTitle,
      value: value.client_id,
      labelSuffix: suffix,
      section: element_type
    }
  }
}